/**
 * #345 — everything the "Open orders — this game" panel decides that does not
 * need a store, a clock or a network: which orders belong on the card, and what
 * each row says.
 *
 * The split against `useGameOpenOrdersModel` is store access, not subject
 * matter. Resolving an asset id to a market is a store walk and lives in the
 * model; deciding that a resolved market's missing `betTeam` falls back to
 * `yesOutcome` then `subtitle` then an em dash is a display decision and lives
 * here, where it is testable by calling a function.
 *
 * The one thing to keep straight while reading: this module produces the
 * DISPLAY view. The destructive scope a bulk cancel is sent from is derived
 * separately by `selectExactGameOrders` under a stricter rule, deliberately
 * not reused from here — see `kalshiOrderMatchesPanel` on why a row can be
 * legible enough to draw and still not be proof of identity for a DELETE.
 */
import { describeMarket } from '../../lib/kalshiTicker';
import { ticketLabel } from '@/components/cards/cardFormat';
import { mirrorCents } from '@/lib/pricing/subCent';
import { orderScopeGameKey } from '@/services/analyticsGameKey';
import {
  isOscarBlockerOrder,
  releasePrefillFromBlocker,
  type OscarReleasePrefill,
} from './oscar';
import type { FrozenCancelSnapshot } from '@/lib/trading/gameOrderSelection';
import { describeCancelVenues, describeSkippedPinned } from '@/lib/trading/gameOrderSelection';
import type { KalshiOrder } from '@/types/kalshi';
import type { PolyRelayOpenOrder } from '@/lib/polymarket/relay';
import { usdToCents } from '@/lib/polymarketUs/gameMapping';
import type { UsGameOrderRow } from '@/lib/polymarketUs/gameOpenOrders';
import { presentUsOrder, usCancelView } from '@/lib/polymarketUs/portfolioModel';
import { exclusionReason } from '../PositionTracker/usBulkCancelModel';

// ── Which orders belong on this card ────────────────────────────────

/**
 * Whether a Kalshi open order belongs to the game a panel renders.
 *
 * `canonKey` is the order's market resolved to a v2 canonical game key
 * (`promoteToGameKeyV2(getMarket(ticker).canonicalGameKey…)`), or '' when the
 * market frame hasn't streamed in yet. `panelKey` is the panel's `game.key`,
 * which for a spread/total/multi panel is a SYNTHETIC key (`<eventKey>|spread|…`),
 * and for a Game Lines period/event panel a one-segment one (`<gameKey>|1H-ml`) —
 * so we compare against its suffix-stripped base (`orderScopeGameKey`, #1068),
 * never the raw synthetic key. Without this, siblings match only in the base
 * moneyline panel and vanish from every spread/total/period panel (#68 review,
 * Brendan). `fallbackTickers`
 * (the panel's own away/home line tickers) still catch a market whose frame is absent.
 *
 * That fallback is exactly why this predicate must never gate a DELETE: a ticker
 * match says "this is one of the two books this card is drawn from", which is
 * enough to render a row and is not enough to prove which game's order it is.
 */
export function kalshiOrderMatchesPanel(
  canonKey: string,
  ticker: string,
  panelKey: string,
  fallbackTickers: Set<string>,
): boolean {
  const base = orderScopeGameKey(panelKey);
  return (canonKey !== '' && canonKey === base) || fallbackTickers.has(ticker);
}

/**
 * Polymarket variant of {@link kalshiOrderMatchesPanel}. Display-only rows have no
 * ticker fallback — an unresolved poly asset can't be placed on the card anyway, so
 * a missing frame ('' canon) simply doesn't match.
 */
export function polyOrderMatchesPanel(canonKey: string, panelKey: string): boolean {
  return canonKey !== '' && canonKey === orderScopeGameKey(panelKey);
}

// ── What each row says ──────────────────────────────────────────────

/** One Kalshi row, fully resolved: the component renders these fields and
 *  derives nothing further. */
export interface KalshiOpenOrderRow {
  orderId: string;
  /** Sentence naming the order, used by the per-row cancel confirmation and
   *  pin: the whole bet first (#964, `ticketLabel`). */
  label: string;
  /** The MARKET's named (YES) team — not the trader's effective backing. A NO
   *  bet on a LAD market reads `(LAD) NO`. '' when the ticker won't parse. For
   *  an NFL ladder prop this is the badge's player + rung; the stat is
   *  `marketType`, shown under the badge so the one-line row never clips. */
  betTeam: string;
  /** #964 — an NFL ladder prop's stat ("Receiving Yards"); null otherwise. */
  marketType: string | null;
  side: 'yes' | 'no';
  action: string;
  remainingCount: number;
  /** Side-native price. Null renders an em dash rather than a guess. */
  sidePriceCents: number | null;
  /** Placed by an oscar sequence as a blocker leg — earns the BLK chip. */
  isBlocker: boolean;
  /** Non-null only when this blocker can be released AND the caller said the
   *  release control is available. The gate is applied here so access and
   *  renderability are one decision, not two that can disagree. */
  releasePrefill: OscarReleasePrefill | null;
}

/**
 * Shape one Kalshi open order into its row.
 *
 * `releaseEnabled` folds together the trader's oscar access and whether the
 * panel supplied a release handler at all; false simply means no prefill, and
 * the BLK chip still shows.
 */
export function projectKalshiOpenOrderRow(
  order: KalshiOrder,
  { releaseEnabled }: { releaseEnabled: boolean },
): KalshiOpenOrderRow {
  const { marketLabel, betTeam, badgeTeam, marketType } = describeMarket(order.ticker, order.side);
  const isBlocker = isOscarBlockerOrder(order);
  return {
    orderId: order.orderId,
    label: ticketLabel(marketLabel, betTeam),
    betTeam: badgeTeam ?? betTeam ?? '',
    marketType: marketType ?? null,
    side: order.side,
    action: order.action,
    remainingCount: order.remainingCount,
    sidePriceCents: sidePriceCentsOf(order),
    isBlocker,
    releasePrefill: isBlocker && releaseEnabled ? releasePrefillFromBlocker(order) : null,
  };
}

/**
 * A Kalshi order's price on its own side.
 *
 * Kalshi often reports only `yes_price`, so a 58¢ NO arrives as `yesPrice: 42`.
 * Reading the first non-null field would render the inverted price; the mirror
 * (100 − p, sub-cent exact) is the only correct read. Null when neither side
 * carries a price — the row shows an em dash instead of inventing one.
 */
function sidePriceCentsOf(
  order: Pick<KalshiOrder, 'side' | 'yesPrice' | 'noPrice'>,
): number | null {
  return order.side === 'yes'
    ? order.yesPrice ?? (order.noPrice != null ? mirrorCents(order.noPrice) : null)
    : order.noPrice ?? (order.yesPrice != null ? mirrorCents(order.yesPrice) : null);
}

/** One Polymarket row. Display-only — there is no cancel control on these. */
export interface PolyOpenOrderRow {
  key: string;
  /** '—' when nothing in the fallback chain names a team. */
  betTeam: string;
  side: 'yes' | 'no' | null;
  action: string;
  /** '—' when the CLOB size won't parse. Sizes are fractional on Polymarket. */
  sizeLabel: string;
  /** Cents, sub-cent precision preserved (0.6250 → 62.5). */
  priceCents: number | null;
}

/** The market fields a poly row falls back to when the order describer can't
 *  name a team. Structural on purpose — the caller passes a `HydraMarket`,
 *  whose optional strings are nullable; `??` skips null and undefined alike,
 *  so both spellings fall through to the next candidate. */
export interface PolyRowMarketContext {
  yesOutcome?: string | null;
  subtitle?: string | null;
}

/**
 * Shape one Polymarket open order into its row, given the market it resolved to
 * and whatever the order describer could say about the asset.
 *
 * Resolution (asset id → ticker → market → canonical key) is the caller's job;
 * by the time an order reaches here it is already known to belong to this game.
 */
export function projectPolyOpenOrderRow(
  order: PolyRelayOpenOrder,
  {
    assetId,
    market,
    description,
  }: {
    assetId: string;
    market: PolyRowMarketContext;
    description: { betTeam?: string; side?: 'yes' | 'no' } | null;
  },
): PolyOpenOrderRow {
  const priceNum = typeof order.price === 'string' ? Number(order.price) : order.price;
  const sizeNum = typeof order.size === 'string' ? Number(order.size) : order.size;
  return {
    // CLOB payloads have carried all three id spellings; the asset id is the
    // last resort so a row always has a stable React key.
    key: String(order.id ?? order.orderID ?? order.orderId ?? assetId),
    betTeam: description?.betTeam ?? market.yesOutcome ?? market.subtitle ?? '—',
    side: description?.side ?? null,
    action: String(order.side ?? '').toLowerCase(),
    sizeLabel: typeof sizeNum === 'number' && Number.isFinite(sizeNum) ? String(sizeNum) : '—',
    priceCents:
      typeof priceNum === 'number' && Number.isFinite(priceNum)
        ? Math.round(priceNum * 10000) / 100
        : null,
  };
}

/** One Polymarket US row (#1016). View-only, like the Polymarket rows: no
 *  inline cancel, repeat, pin or release here; the game sweep is the action. */
export interface UsOpenOrderRow {
  /** Venue + account + intent: an intent id is unique only within its account. */
  key: string;
  accountTail: string;
  label: string;
  outcome: 'yes' | 'no';
  action: 'buy' | 'sell';
  /** Exact decimal text, or 'Unavailable' — an unknown remainder is never 0. */
  remaining: string;
  /** The selected outcome's own price (a NO order carries its NO price). */
  priceCents: number;
  status: string;
  /** Why this order is not in the game sweep, or what its last cancel
   *  established; null when it would be swept. Shown in place of `status`. */
  note: string | null;
}

/** Shape one exact-game US order through the shared US presenter. */
export function projectUsOpenOrderRow(order: UsGameOrderRow, nowMs: number): UsOpenOrderRow {
  const view = presentUsOrder(order.row);
  const cancel = usCancelView(order.row, order.readiness, nowMs);
  const note = order.expired
    ? 'Not included — market listing lapsed'
    : cancel.status ?? (cancel.canCancel ? null : `Not included — ${exclusionReason(cancel.blockedReason, null)}`);
  return {
    key: `polymarket_us:${order.accountId}:${order.row.intentId}`,
    accountTail: order.accountId.slice(-4),
    label: order.row.target.label,
    outcome: order.row.target.outcome,
    action: order.row.action,
    remaining: view.remaining,
    priceCents: usdToCents(view.price),
    status: view.status,
    note,
  };
}

// ── The confirmation sentence ───────────────────────────────────────

/** The frozen confirmation sentence. Reads ONLY from the snapshot — never from a
 *  live order array — so what the trader approves is exactly what is sent.
 *  `usTargets` is the frozen Polymarket US count (#1016), sent beside it. */
export function buildGameCancelMessage(snapshot: FrozenCancelSnapshot, usTargets = 0): string {
  const n = snapshot.targets.length + usTargets;
  const skipped = describeSkippedPinned(snapshot);
  const venues = usTargets === 0
    ? describeCancelVenues(snapshot.venues)
    : snapshot.venues.length === 0
      ? 'Polymarket US'
      : `${describeCancelVenues(snapshot.venues).replace(' and ', ', ')} and Polymarket US`;
  return (
    `Cancel ${n} open order${n === 1 ? '' : 's'} for ${snapshot.label}? ` +
    `Venues: ${venues}. ` +
    'Only the listed order IDs will be submitted; later updates cannot add orders.' +
    (skipped ? ` ${skipped}` : '')
  );
}
